import { useEffect } from 'react';
import { Outlet, useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  FolderKanban,
  FileBarChart,
  Settings,
  LogOut,
  UserCog,
  HeartHandshake,
  ShoppingCart,
  MessageSquareWarning,
} from 'lucide-react';
import { BrandLogo } from '@/components/branding/BrandLogo';

const navItems = [
  { to: '/app/dashboard', label: 'Inicio', icon: LayoutDashboard },
  { to: '/app/processes', label: 'Procesos', icon: FolderKanban },
  { to: '/app/results', label: 'Resultados', icon: FileBarChart },
  { to: '/app/complaints', label: 'Buzón de Quejas', icon: MessageSquareWarning },
  { to: '/app/nom035', label: 'Desarrollo Organizacional', icon: HeartHandshake },
  { to: '/app/billing', label: 'Facturación', icon: ShoppingCart },
  { to: '/app/settings', label: 'Ajustes', icon: Settings },
];

export function AppLayout() {
  const { appUser, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (appUser?.role === 'owner') {
      navigate('/owner/dashboard', { replace: true });
    }
  }, [appUser?.role, navigate]);

  if (appUser?.role === 'owner') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Redirigiendo...</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-muted/30">
      <aside className="flex w-56 flex-col border-r bg-card">
        <div className="flex h-14 items-center gap-2 border-b px-4">
          <BrandLogo variant="isotipo" size={32} alt="PsyTalent" />
          <span className="font-semibold">PsyTalent</span>
        </div>
        <nav className="flex flex-1 flex-col gap-1 p-2">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/app/team"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                )
              }
            >
              <UserCog className="h-4 w-4" />
              Equipo
            </NavLink>
          )}
        </nav>
        <div className="border-t p-2">
          <div className="truncate px-3 py-2 text-xs text-muted-foreground">
            {appUser?.displayName || appUser?.email}
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start gap-2" onClick={() => signOut()}>
            <LogOut className="h-4 w-4" />
            Salir
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto p-6">
        <Outlet />
      </main>
    </div>
  );
}
